import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { adminApiMessage } from '../adminApi';
import { EvalBadge, EvalError } from '../evals/EvalBits';
import {
  CASES_HREF,
  CONSENT_SCOPE,
  basisProblems,
  boatOwners,
  consentBasisTemplate,
  consentBoatText,
  consentUserText,
  corpusKeyProblem,
  revokedReasonText,
  suggestCorpusKey,
} from '../evals/inbox';
import { inboxApi } from '../evals/inboxApi';
import type {
  AdminBoatRow,
  AdminUserRow,
  EvalConsentRevokeOutcome,
  EvalMomentConsent,
} from '../evals/inboxTypes';
import { GuideLink } from '../evals/GuideLink';
import { formatDate } from '../format';

// Recorded-moment consent (TOM-140, ADR 0233 §6 as amended by ADR 0253): the
// list of grants, active and revoked, with why each revoked one ended; recording
// an owner's grant for their boat; and revoking one, which lists what the
// committed corpus holds under its key for the removal PR. Version 1 is the
// operator's own boats. Every grant and revoke is audited fail-closed.

/** Today in the admin's own time zone, as YYYY-MM-DD. */
function localDay(d = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function GrantForm({
  boats,
  users,
  consents,
  onGranted,
}: {
  boats: AdminBoatRow[];
  users: AdminUserRow[];
  consents: EvalMomentConsent[];
  onGranted: (c: EvalMomentConsent) => void;
}) {
  const [boatId, setBoatId] = useState('');
  const [ownerUserId, setOwnerUserId] = useState('');
  const [corpusKey, setCorpusKey] = useState('');
  const [basis, setBasis] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeBoats = useMemo(
    () => new Set(consents.filter((c) => c.active && c.boatId !== null).map((c) => c.boatId)),
    [consents]
  );
  const boat = boats.find((b) => b.id === boatId);
  const owners = boat !== undefined ? boatOwners(boat, users) : [];
  const owner = owners.find((o) => o.userId === ownerUserId);
  const keyProblem = corpusKeyProblem(corpusKey);
  const problems = basisProblems(basis);

  function pickBoat(id: string) {
    setBoatId(id);
    const next = boats.find((b) => b.id === id);
    const nextOwners = next !== undefined ? boatOwners(next, users) : [];
    const first = nextOwners[0];
    setOwnerUserId(first?.userId ?? '');
    setCorpusKey(suggestCorpusKey(next?.display_name ?? null));
    setBasis(
      next !== undefined && first !== undefined
        ? consentBasisTemplate({
            ownerEmail: first.email,
            boatName: next.display_name ?? `boat ${next.id.slice(0, 8)}`,
            date: localDay(),
          })
        : ''
    );
  }

  function pickOwner(id: string) {
    setOwnerUserId(id);
    const o = owners.find((x) => x.userId === id);
    if (boat !== undefined && o !== undefined) {
      setBasis(
        consentBasisTemplate({
          ownerEmail: o.email,
          boatName: boat.display_name ?? `boat ${boat.id.slice(0, 8)}`,
          date: localDay(),
        })
      );
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (boat === undefined || owner === undefined || keyProblem !== null || problems.length > 0) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { consent } = await inboxApi.grantConsent({
        boatId: boat.id,
        ownerUserId: owner.userId,
        corpusKey,
        basis: basis.trim(),
      });
      onGranted(consent);
    } catch (err) {
      setError(adminApiMessage(err, 'The consent was not recorded.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="eval-card eval-inbox-grant"
      onSubmit={(e) => void submit(e)}
      data-testid="eval-grant-form"
    >
      <header className="eval-card-header">
        <h3>Record an owner&apos;s consent</h3>
      </header>
      <p className="admin-muted eval-sub">
        Only for a boat whose owner has agreed, and in version 1 only the operator&apos;s own boats:
        Gallivant RP in production, AnchorTest in development. A grant is never edited: to change
        one, revoke it and record a new one.
      </p>
      <div className="eval-inbox-row">
        <div className="eval-inbox-field">
          <label htmlFor="eval-grant-boat" className="eval-inbox-label">
            Boat
          </label>
          <select
            id="eval-grant-boat"
            value={boatId}
            onChange={(e) => pickBoat(e.target.value)}
            data-testid="eval-grant-boat"
          >
            <option value="">Pick a boat</option>
            {boats.map((b) => (
              <option key={b.id} value={b.id} disabled={activeBoats.has(b.id)}>
                {b.display_name ?? 'Unnamed boat'} · {b.id.slice(0, 8)}
                {b.owner_emails.length > 0 ? ` · ${b.owner_emails.join(', ')}` : ' · no owner'}
                {activeBoats.has(b.id) ? ' · has a consent' : ''}
                {b.decommissioned_at !== null ? ' · decommissioned' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="eval-inbox-field">
          <label htmlFor="eval-grant-owner" className="eval-inbox-label">
            Owner who agreed
          </label>
          <select
            id="eval-grant-owner"
            value={ownerUserId}
            disabled={boat === undefined}
            onChange={(e) => pickOwner(e.target.value)}
            data-testid="eval-grant-owner"
          >
            {owners.length === 0 ? <option value="">No owner to name</option> : null}
            {owners.map((o) => (
              <option key={o.userId} value={o.userId}>
                {o.email}
              </option>
            ))}
          </select>
        </div>
        <div className="eval-inbox-field">
          <label htmlFor="eval-grant-key" className="eval-inbox-label">
            Corpus key
          </label>
          <input
            id="eval-grant-key"
            type="text"
            value={corpusKey}
            maxLength={32}
            placeholder="e.g. anchortest"
            onChange={(e) => setCorpusKey(e.target.value.trim())}
            data-testid="eval-grant-key"
          />
          <span className="admin-muted eval-sub">
            The name the boat&apos;s moment files carry. It names this boat in every deployment, for
            good.
          </span>
        </div>
      </div>
      <div className="eval-inbox-field">
        <label htmlFor="eval-grant-basis" className="eval-inbox-label">
          Basis: how the owner agreed
        </label>
        <textarea
          id="eval-grant-basis"
          value={basis}
          rows={3}
          maxLength={500}
          onChange={(e) => setBasis(e.target.value)}
          data-testid="eval-grant-basis"
        />
        <span className="admin-muted eval-sub">
          Say how and when the owner agreed, and that they agreed to moments holding the boat&apos;s
          position and track.
        </span>
      </div>
      {boat !== undefined && (keyProblem !== null || problems.length > 0) ? (
        <ul className="eval-list" data-testid="eval-grant-problems">
          {keyProblem !== null ? <li>{keyProblem}</li> : null}
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      ) : null}
      <EvalError message={error} />
      <div className="eval-actions">
        <button
          type="submit"
          className="admin-primary-button admin-button-inline"
          disabled={
            busy ||
            boat === undefined ||
            owner === undefined ||
            keyProblem !== null ||
            problems.length > 0
          }
          data-testid="eval-grant-submit"
        >
          {busy ? 'Recording…' : 'Record the consent'}
        </button>
      </div>
    </form>
  );
}

function RevokeOutcome({ outcome }: { outcome: EvalConsentRevokeOutcome }) {
  const { committed, inboxCaseIds, consent } = outcome;
  const nothing =
    committed.momentIds.length === 0 && committed.caseIds.length === 0 && inboxCaseIds.length === 0;
  return (
    <div className="eval-upload-result" role="status" data-testid="eval-revoke-outcome">
      <p>
        Revoked the consent for <code>{consent.corpusKey}</code>. From now on no draft, accept or
        export uses a moment under it.
      </p>
      {nothing ? (
        <p>Nothing in the corpus or the inbox uses it.</p>
      ) : (
        <>
          <p>
            Remove these in one PR within 30 days, as the revoke runbook in{' '}
            <code>docs/admin-console.md</code> says:
          </p>
          <dl className="eval-kv">
            <dt>Committed moments</dt>
            <dd>{committed.momentIds.length > 0 ? committed.momentIds.join(', ') : '—'}</dd>
            <dt>Committed cases</dt>
            <dd>{committed.caseIds.length > 0 ? committed.caseIds.join(', ') : '—'}</dd>
            <dt>Inbox cases</dt>
            <dd>
              {inboxCaseIds.length > 0
                ? `${inboxCaseIds.join(', ')}: move each back to drafting on the stub boat, or reject it`
                : '—'}
            </dd>
          </dl>
        </>
      )}
    </div>
  );
}

function ConsentCard({
  c,
  boats,
  users,
  busy,
  onRevoke,
}: {
  c: EvalMomentConsent;
  boats: AdminBoatRow[];
  users: AdminUserRow[];
  busy: boolean;
  onRevoke: (c: EvalMomentConsent) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  return (
    <article
      className="eval-card eval-inbox-consent"
      data-testid={`eval-consent-${c.id}`}
      data-corpus-key={c.corpusKey}
    >
      <header className="eval-card-header">
        <span className="eval-stack">
          <h3 className={c.boatId === null ? 'admin-muted' : undefined}>
            {consentBoatText(c.boatId, boats)}
          </h3>
          <span className="admin-muted eval-sub">
            Corpus key <code>{c.corpusKey}</code>
            {c.boatId !== null ? (
              <>
                {' '}
                · boat <code>{c.boatId.slice(0, 8)}</code>
              </>
            ) : null}
          </span>
        </span>
        <span data-testid="eval-consent-state">
          {c.active ? (
            <EvalBadge tone="succeeded">Active</EvalBadge>
          ) : (
            <EvalBadge tone="muted">Revoked</EvalBadge>
          )}
        </span>
      </header>
      <dl className="eval-kv">
        <dt>Owner</dt>
        <dd className={c.ownerUserId === null ? 'admin-muted' : undefined}>
          {c.ownerUserId !== null ? (
            <Link to={`/admin/users/${c.ownerUserId}`}>
              {consentUserText(c.ownerUserId, users)}
            </Link>
          ) : (
            consentUserText(null, users)
          )}
        </dd>
        <dt>Basis</dt>
        <dd data-testid="eval-consent-basis">{c.basis}</dd>
        <dt>Granted</dt>
        <dd>
          {formatDate(c.grantedAt)}, by {consentUserText(c.grantedBy, users, 'a deleted admin')}
        </dd>
        {!c.active ? (
          <>
            <dt>Revoked</dt>
            <dd>
              <span className="eval-stack">
                <span data-testid="eval-consent-reason">{revokedReasonText(c.revokedReason)}</span>
                <span className="admin-muted eval-sub">
                  {formatDate(c.revokedAt)}, by{' '}
                  {consentUserText(c.revokedBy, users, 'a deleted admin')}
                </span>
              </span>
            </dd>
          </>
        ) : null}
      </dl>
      {c.active ? (
        confirming ? (
          <div className="eval-callout eval-inbox-revoke-confirm" role="group" aria-label="Revoke">
            <p>
              No draft, accept or export uses a moment under <code>{c.corpusKey}</code> from then
              on. What the corpus already holds under it is listed for a removal PR.
            </p>
            <div className="eval-actions">
              <button
                type="button"
                className="admin-button-danger"
                disabled={busy}
                onClick={() => onRevoke(c)}
                data-testid="eval-revoke-confirm"
              >
                Revoke the consent
              </button>
              <button
                type="button"
                className="admin-button-secondary"
                disabled={busy}
                onClick={() => setConfirming(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="eval-actions">
            <button
              type="button"
              className="admin-button-secondary"
              onClick={() => setConfirming(true)}
              data-testid="eval-revoke-start"
            >
              Revoke…
            </button>
          </div>
        )
      ) : null}
    </article>
  );
}

export function AdminEvalConsents() {
  const [consents, setConsents] = useState<EvalMomentConsent[] | null>(null);
  const [boats, setBoats] = useState<AdminBoatRow[]>([]);
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [granting, setGranting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [revoked, setRevoked] = useState<EvalConsentRevokeOutcome | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await inboxApi.consents();
      setConsents(res.consents);
    } catch (err) {
      setError(adminApiMessage(err, 'Failed to load the consents.'));
    }
  }, []);

  useEffect(() => {
    void load();
    // The boats and people name each row and fill the grant form. Without them
    // the rows still show, by id.
    inboxApi
      .boats()
      .then((r) => setBoats(r.boats))
      .catch(() => setBoats([]));
    inboxApi
      .users()
      .then((r) => setUsers(r.users))
      .catch(() => setUsers([]));
  }, [load]);

  async function revoke(c: EvalMomentConsent) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const outcome = await inboxApi.revokeConsent(c.id);
      setRevoked(outcome);
      await load();
    } catch (err) {
      setError(adminApiMessage(err, 'The consent was not revoked.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="admin-section eval-page eval-inbox">
      <p>
        <Link to={CASES_HREF}>← Eval cases</Link>
      </p>
      <header className="admin-section-header">
        <h1>Recorded-moment consents</h1>
        <span className="admin-count">
          {consents !== null
            ? `${consents.filter((c) => c.active).length} active, ${consents.filter((c) => !c.active).length} revoked`
            : null}
        </span>
        <GuideLink section="consents" />
      </header>
      <p className="admin-hint">
        A case runs on the stub boat unless its turn&apos;s boat is here with an active consent.
      </p>
      <div className="eval-callout eval-inbox-scope" data-testid="eval-consent-scope">
        <strong>What a grant covers.</strong> {CONSENT_SCOPE} A grant covers all of it, the position
        and the track included.
      </div>

      <EvalError message={error} />
      {notice !== null ? (
        <div className="eval-upload-result" role="status" data-testid="eval-grant-notice">
          {notice}
        </div>
      ) : null}
      {revoked !== null ? <RevokeOutcome outcome={revoked} /> : null}

      <div className="eval-actions">
        {granting ? null : (
          <button
            type="button"
            className="admin-button-secondary"
            onClick={() => {
              setGranting(true);
              setNotice(null);
              setRevoked(null);
            }}
            data-testid="eval-grant-start"
          >
            Record a consent…
          </button>
        )}
      </div>
      {granting && consents !== null ? (
        <GrantForm
          boats={boats}
          users={users}
          consents={consents}
          onGranted={(c) => {
            setGranting(false);
            setNotice(`Recorded the consent for ${c.corpusKey}.`);
            void load();
          }}
        />
      ) : null}

      {consents === null && error === null ? <p className="admin-loading-row">Loading…</p> : null}
      {consents !== null ? (
        consents.length === 0 ? (
          <div className="admin-empty-state" data-testid="eval-consents-empty">
            <p className="admin-empty-state-title">No consent recorded.</p>
            <p className="admin-hint">Every case runs on the stub boat.</p>
          </div>
        ) : (
          <div className="eval-cards eval-inbox-consents" data-testid="eval-consents-list">
            {consents.map((c) => (
              <ConsentCard
                key={c.id}
                c={c}
                boats={boats}
                users={users}
                busy={busy}
                onRevoke={(x) => void revoke(x)}
              />
            ))}
          </div>
        )
      ) : null}
    </section>
  );
}
